// =============================================================================
// WhatsAppCTA — Global reusable "Contact via WhatsApp" button.
// Always reads the WA number from GET /api/public/community (admin-editable).
// Falls back to +60137003381 (the platform default from the Constitution).
// =============================================================================
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { MessageCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_WA = "60137003381"; // digits only

function useWhatsAppNumber() {
  const [num, setNum] = useState(DEFAULT_WA);
  useEffect(() => {
    axios
      .get(`${BACKEND}/api/public/community`)
      .then((r) => {
        const raw = (r?.data?.whatsapp_number || "").toString().replace(/\D/g, "");
        if (raw) setNum(raw);
      })
      .catch(() => {});
  }, []);
  return num;
}

export function openWhatsApp(number, text) {
  const digits = (number || DEFAULT_WA).replace(/\D/g, "");
  const t = encodeURIComponent(text || "Hi MIRESCO!");
  window.open(`https://wa.me/${digits}?text=${t}`, "_blank", "noopener");
}

/**
 * Props:
 *   template     - WA message text (falls back to a generic hello)
 *   label        - Button text (default: "WhatsApp Us")
 *   variant      - "solid" | "outline" | "compact"
 *   accent       - "green" (default) | "red" | "gold" | "charcoal" | custom hex
 *   testId       - data-testid override
 *   overrideNumber - override the CMS-fetched number (e.g. opportunity-specific)
 */
export default function WhatsAppCTA({
  template = "",
  label = "WhatsApp Us",
  variant = "solid",
  accent = "green",
  testId = "whatsapp-cta",
  overrideNumber = "",
  className = "",
}) {
  const globalNum = useWhatsAppNumber();
  const num = overrideNumber && overrideNumber.trim() ? overrideNumber : globalNum;

  const bgMap = {
    green: "#25D366",
    red: "#C8102E",
    gold: "#D4AF37",
    charcoal: "#1A1A1A",
  };
  const bg = bgMap[accent] || accent;

  const handle = useCallback(() => {
    openWhatsApp(num, template);
  }, [num, template]);

  const baseCls =
    "inline-flex items-center gap-2 font-semibold transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1";

  if (variant === "outline") {
    return (
      <button
        onClick={handle}
        data-testid={testId}
        className={`${baseCls} border-2 px-5 py-2.5 text-sm ${className}`}
        style={{ borderColor: bg, color: bg, background: "white" }}
      >
        <MessageCircle className="w-4 h-4" /> {label}
      </button>
    );
  }
  if (variant === "compact") {
    return (
      <button
        onClick={handle}
        data-testid={testId}
        className={`${baseCls} px-3 py-1.5 text-xs text-white ${className}`}
        style={{ background: bg }}
      >
        <MessageCircle className="w-3.5 h-3.5" /> {label}
      </button>
    );
  }
  return (
    <button
      onClick={handle}
      data-testid={testId}
      className={`${baseCls} px-5 py-3 text-white ${className}`}
      style={{ background: bg }}
    >
      <MessageCircle className="w-4 h-4" /> {label}
    </button>
  );
}
